import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(() => comics.find((c) => c.id === Number(id)), [id]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-4 inline-block" />
          <h1 className="text-xl font-semibold text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-4">抱歉，您要找的漫画不存在或已被移除</p>
          <Link to="/" className="inline-flex items-center gap-1 text-rose-500 hover:underline text-sm">
            <i className="ri-arrow-left-line" /> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = categories.find((c) => c.slug === comic.category)?.name || '';
  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4);
  const chapterList = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(2026, 7 - Math.floor(i / 4), 5 - (i % 4)).toISOString().split('T')[0],
  }));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫星球`,
    description: comic.summary,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-15',
    dateModified: comic.updateTime,
    image: comic.cover,
    publisher: { '@type': 'Organization', name: '韩漫星球' },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <SEO
        title={`${comic.title}_${comic.author}_韩漫免费下拉式阅读_韩漫星球`}
        description={comic.summary}
        keywords={`${comic.title},${comic.tags.join(',')},韩漫免费,漫画下拉式阅读`}
        canonicalUrl={`https://hanmanxingqiu.com/article/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        articlePublishedTime="2026-01-15"
        articleModifiedTime={comic.updateTime}
        jsonLd={jsonLd}
      />

      <div className="bg-white">
        <Navbar />
      </div>

      <main>
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto pt-24 pb-6">
          <Breadcrumb items={[
            { label: '首页', to: '/' },
            { label: `${categoryName}漫画`, to: `/category/${comic.category}` },
            { label: comic.title },
          ]} />
        </div>

        {/* Comic Detail Header */}
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8 bg-white rounded-xl p-5 md:p-8 border border-background-200">
            <div className="flex-shrink-0 w-full md:w-56">
              <img
                src={comic.cover}
                alt={`${comic.title} - 韩漫星球`}
                title={`${comic.title} - ${comic.tags.join(',')}`}
                className="w-full md:w-56 rounded-lg object-cover object-top aspect-[3/4]"
              />
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center gap-1 text-yellow-500 text-sm font-medium">
                  <i className="ri-star-fill" /> {comic.rating}
                </span>
                <span className="text-sm text-foreground-400">
                  <i className="ri-eye-line mr-1" />{comic.views}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${comic.status === '连载中' ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-600'}`}>
                  {comic.status}
                </span>
                <span className="text-sm text-foreground-400">
                  共 {comic.chapters} 话
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">{tag}</span>
                ))}
              </div>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-4">
                <span>作者：<strong className="text-foreground-800">{comic.author}</strong></span>
                <span>分类：<Link to={`/category/${comic.category}`} className="text-rose-500 hover:underline">{categoryName}</Link></span>
                <span>更新：{comic.updateTime}</span>
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">{comic.summary}</p>

              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/article/${comic.id}`}
                  className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-play-circle-line" /> 开始阅读
                </Link>
                <button className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap">
                  <i className="ri-bookmark-line" /> 收藏
                </button>
                <button className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap">
                  <i className="ri-share-forward-line" /> 分享
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Chapter List */}
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto py-10">
          <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                章节列表
              </h2>
              <span className="text-xs text-foreground-400">共 {comic.chapters} 话</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {chapterList.map((ch) => (
                <button
                  key={ch.num}
                  className="flex items-center justify-between px-4 py-2.5 rounded-lg border border-background-200 hover:border-rose-300 hover:bg-rose-50 transition-all duration-200 text-left group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-foreground-950 font-medium whitespace-nowrap">{ch.title}</span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.date}</span>
                </button>
              ))}
            </div>

            {comic.chapters > 20 && (
              <div className="text-center mt-5">
                <button className="text-sm text-rose-500 hover:underline">
                  查看全部 {comic.chapters} 话 <i className="ri-arrow-down-s-line" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-14">
            <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} {...c} />
              ))}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}